import { isPlatformServer } from '@angular/common';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { AdminAdminService } from '@api-client';
import { AuthTokenService, LOG_CONTENT, ToastService } from '@client-libs';
import { catchError, firstValueFrom, from, switchMap, throwError } from 'rxjs';

export const HttpInterceptor: HttpInterceptorFn = (req, next) => {
  const platformId = inject(PLATFORM_ID);
  const toastService = inject(ToastService);
  const router = inject(Router);
  const authTokenService = inject(AuthTokenService);
  const adminService = inject(AdminAdminService);

  if (isPlatformServer(platformId)) {
    return next(req);
  }

  // Clone request with Authorization header if token exists
  const accessToken = authTokenService.getAccessToken();
  const headers: Record<string, string> = {};

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  // LOG_CONTENT가 설정된 경우 x-log-content 헤더 추가
  const logContent = req.context.get(LOG_CONTENT);
  if (logContent) {
    headers['x-log-content'] = encodeURIComponent(logContent);
  }

  const request = req.clone({ setHeaders: headers });

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      // Token expired - attempt refresh
      if (error.status === 401 || error.status === 497) {
        const refreshToken = authTokenService.getRefreshToken();
        if (refreshToken) {
          return from(
            firstValueFrom(
              adminService.adminAdminControllerRefreshV1({
                body: { refreshToken },
              })
            )
          ).pipe(
            switchMap(res => {
              if (res.accessToken) {
                authTokenService.setAccessToken(res.accessToken);
                // Retry original request with new token
                const retryReq = req.clone({
                  setHeaders: {
                    Authorization: `Bearer ${res.accessToken}`,
                  },
                });
                return next(retryReq);
              }
              return throwError(() => error);
            }),
            catchError(() => {
              authTokenService.clearTokens();
              router.navigateByUrl('/sign-in', { replaceUrl: true });
              return throwError(() => error);
            })
          );
        }
        authTokenService.clearTokens();
        router.navigateByUrl('/sign-in', { replaceUrl: true });
        toastService.error(
          '로그인 세션이 만료되었습니다. 다시 로그인해주세요.'
        );
        return throwError(() => error);
      }

      if (error.status === 500 || error.status === 0) {
        toastService.error(
          '서버에 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'
        );
        return throwError(() => error);
      }

      if (error.error?.message) {
        toastService.error(error.error.message);
      }

      return throwError(() => error);
    })
  );
};
